import { CartUpdateEvent } from '@theme/events';

const config = JSON.parse(document.getElementById('cart-automatic-products-config').textContent);
const root = window.Shopify?.routes?.root || '/';
let running;
let dirty = false;
let checkoutReady = false;
let pendingCart;
const declinedAttribute = '_automatic_gifts_declined';
const declinedGifts = new Set((config.initialCart?.attributes?.[declinedAttribute] || '').split(',').filter(Boolean));
const giftKey = (rule, variant) => `${rule}:${variant}`;

async function request(path, body) {
  const response = await fetch(`${root}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json();
  if (!response.ok || result.errors) throw new Error(result.description || 'No se pudieron actualizar los productos automáticos.');
  return result;
}

function hasManualDiscount(cart) {
  if (cart.discount_codes?.some((discount) => discount.applicable === true)) return true;
  const applications = [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.discount_applications || []),
    ...cart.items.flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
  ];
  return applications.some((application) => application?.type === 'discount_code');
}

async function reconcile(snapshot) {
  const cart = snapshot || await request('cart.js');
  // Start a fresh choice after the shopper empties the cart.
  if (!cart.items.length) declinedGifts.clear();
  const gifts = cart.items.filter((item) => item.properties?._cart_automatic_rule);
  const subtotal = cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0);
  const manualDiscount = hasManualDiscount(cart);
  const hasProducts = cart.items.some((item) => !item.properties?._cart_automatic_rule);
  // A rejection lasts until this rule drops below its own spending threshold.
  for (const rule of config.rules) {
    if (subtotal < rule.minimum) declinedGifts.delete(giftKey(rule.id, rule.variant));
  }
  const declinedValue = [...declinedGifts].sort().join(',');
  const attributesChanged = (cart.attributes?.[declinedAttribute] || '') !== declinedValue;
  const desired = config.enabled && hasProducts && !manualDiscount
    ? config.rules.filter((rule) => rule.enabled && rule.variant && rule.available && subtotal >= rule.minimum && !declinedGifts.has(giftKey(rule.id, rule.variant)))
    : [];
  if (config.debug) {
    const status = document.getElementById('cart-automatic-products-status');
    if (status) {
      const amount = new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency: cart.currency || 'EUR',
      }).format(subtotal / 100);
      const reasons = config.rules.map((rule) => {
        const reason = manualDiscount ? 'incompatible con el código de descuento aplicado'
          : declinedGifts.has(giftKey(rule.id, rule.variant)) ? 'rechazado por el cliente'
          : !rule.enabled ? 'desactivado'
          : !rule.variant ? 'sin variante válida (revisa el producto, el ID de variante, el stock y la publicación)'
          : !rule.available ? 'sin stock'
          : !hasProducts ? 'sin productos de compra en el carrito'
          : subtotal < rule.minimum ? `no alcanza el mínimo de ${rule.minimum / 100}`
          : 'cumple las condiciones';
        return `Producto ${rule.id}: ${reason}.`;
      });
      status.textContent = `Productos automáticos — ${config.enabled ? 'activados' : 'ACTIVACIÓN GENERAL DESACTIVADA'}. Subtotal evaluado: ${amount}. ${reasons.join(' ')}`;
      status.hidden = false;
    }
  }
  const updates = {};
  const additions = [];
  const matched = new Set();
  for (const item of gifts) {
    const rule = desired.find((candidate) => candidate.id === item.properties._cart_automatic_rule && candidate.variant === item.variant_id);
    const quantity = rule && !matched.has(rule.id) ? rule.quantity : 0;
    if (rule) matched.add(rule.id);
    if (item.quantity !== quantity) updates[item.key] = quantity;
  }
  for (const rule of desired) {
    if (!matched.has(rule.id)) additions.push({ id: rule.variant, quantity: rule.quantity, properties: { _cart_automatic_rule: rule.id } });
  }
  if (!Object.keys(updates).length && !additions.length && !attributesChanged) return;
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))].slice(0, 5);
  let result;
  if (Object.keys(updates).length || attributesChanged) result = await request('cart/update.js', { updates, ...(attributesChanged ? { attributes: { [declinedAttribute]: declinedValue } } : {}), sections, sections_url: location.pathname });
  if (additions.length) result = await request('cart/add.js', { items: additions, sections, sections_url: location.pathname });
  const updatedCart = await request('cart.js');
  document.dispatchEvent(new CartUpdateEvent(updatedCart, 'cart-automatic-products', {
    source: 'cart-automatic-products', itemCount: updatedCart.item_count, sections: result.sections,
  }));
}

function synchronize(snapshot) {
  pendingCart = snapshot?.items ? snapshot : undefined;
  dirty = true;
  if (running) return running;
  running = (async () => {
    const error = document.getElementById('cart-automatic-products-error');
    try {
      while (dirty) {
        dirty = false;
        const snapshot = pendingCart;
        pendingCart = undefined;
        await reconcile(snapshot);
      }
      error.hidden = true;
      return true;
    } catch (cause) {
      error.textContent = config.errorMessage;
      error.hidden = false;
      console.error('Productos automáticos:', cause);
      return false;
    } finally {
      running = null;
    }
  })();
  return running;
}

window.addEventListener('cart:update', (event) => {
  if (event.detail?.sourceId !== 'cart-automatic-products') synchronize(event.detail?.resource);
});
window.addEventListener('discount:update', (event) => synchronize(event.detail?.resource));
window.addEventListener('pageshow', (event) => {
  if (event.persisted) synchronize();
});
// Delegate removal so it continues working after cart sections are replaced.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-remove-automatic-gift]');
  if (!button) return;
  event.preventDefault();
  if (button.disabled) return;
  button.disabled = true;
  declinedGifts.add(giftKey(button.dataset.giftRule, button.dataset.giftVariant));
  await synchronize();
  // Keep removal retryable if Shopify rejects the request.
  button.disabled = false;
});

// Wait for gift reconciliation before navigating to checkout.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[name="checkout"], a[href*="/checkout"]');
  if (!button || checkoutReady) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (await synchronize()) {
    checkoutReady = true;
    button.click();
    checkoutReady = false;
  }
}, true);
synchronize(config.initialCart);
